import React, { useMemo, useState } from "react";
import {
  X,
  HelpCircle,
  Rocket,
  CalendarCheck,
  Target,
  Bell,
  ListChecks,
  Clipboard,
  Link2,
  GraduationCap,
  BookOpen,
  StickyNote,
  Timer,
  Cloud,
  Keyboard,
  Smartphone,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  KeyRound,
} from "lucide-react";

// Every section of the guide. `body` is rendered inside the panel; `text`
// powers the search box (so a question typed in plain words finds the answer).
const H = ({ children }) => (
  <h4 className="text-cyan-300 font-semibold mt-4 mb-1.5 first:mt-0">
    {children}
  </h4>
);
const P = ({ children }) => (
  <p className="text-gray-300 text-sm leading-relaxed mb-2">{children}</p>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-5 space-y-1 mb-2">{children}</ul>
);
const LI = ({ children }) => (
  <li className="text-gray-300 text-sm leading-relaxed">{children}</li>
);
const Key = ({ children }) => (
  <kbd className="px-1.5 py-0.5 rounded-md bg-gray-700 border border-gray-600 text-cyan-200 text-[11px] font-mono">
    {children}
  </kbd>
);
const Tip = ({ children }) => (
  <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/30 p-3 text-cyan-100 text-sm leading-relaxed mb-2">
    {children}
  </div>
);

const SECTIONS = [
  {
    id: "start",
    label: "Getting started",
    icon: Rocket,
    body: (
      <>
        <P>
          Xunzhine turns one long goal into a run of days you can actually see.
          You set a challenge — a name, a start date and a number of days — and
          then mark each day as you live it. Everything else on the page (goals,
          reminders, notes, links) hangs off that spine.
        </P>
        <H>Signing in</H>
        <P>The sign-in screen offers two ways in:</P>
        <UL>
          <LI>
            <strong>Continue (only with pro plan)</strong> — enter the username
            and passkey of your Pro (cloud) account. Your data then syncs across
            every browser and the Android app, and version history is kept. The
            passkey is checked by the server and never stored on your device;
            only a sign-in session is kept, so you stay signed in until you sign
            out.
          </LI>
          <LI>
            <strong>Use local storage only (no cloud sync)</strong> — type just
            a username. Every feature works, but your data lives in this browser
            alone.
          </LI>
        </UL>
        <H>Your first five minutes</H>
        <UL>
          <LI>
            Open <strong>Settings</strong> (gear, top right) and set the
            challenge name, start date and number of days.
          </LI>
          <LI>
            Add a few goals in the <strong>Goals</strong> panel — one line each,
            or paste a whole list at once.
          </LI>
          <LI>
            At the end of the day press <strong>Mark today</strong> in the
            header. Press it again to cycle: done → partial → not done.
          </LI>
        </UL>
        <Tip>
          Nothing needs a "save" button. Every change is stored as you make it
          and, when you are signed in, synced to your other devices within a
          second or two.
        </Tip>
      </>
    ),
  },
  {
    id: "days",
    label: "Days, streak & calendar",
    icon: CalendarCheck,
    body: (
      <>
        <H>Marking a day</H>
        <P>
          <strong>Mark today</strong> in the header cycles through three states:
          not marked → <span className="text-green-300">done</span> →{" "}
          <span className="text-yellow-300">partial</span> → not marked. Tap any
          square in the calendar to do the same for that day (useful when you
          forget to mark one).
        </P>
        <H>What the numbers mean</H>
        <UL>
          <LI>
            <strong>done</strong> — days marked complete.
          </LI>
          <LI>
            <strong>left</strong> — days of the challenge still to go.
          </LI>
          <LI>
            <strong>streak</strong> — completed days in an unbroken run ending
            today. A partial day does not continue a streak.
          </LI>
          <LI>
            <strong>best</strong> — your longest streak so far.
          </LI>
          <LI>
            <strong>missed</strong> — days already past that are neither done
            nor partial.
          </LI>
        </UL>
        <P>
          On a wide screen these sit on the header row; on a narrower one they
          hide behind the small chart button next to the cloud icon.
        </P>
        <H>Calendar colours</H>
        <UL>
          <LI>
            <span className="text-green-300">Green</span> — done ·{" "}
            <span className="text-yellow-300">amber</span> — partial ·{" "}
            <span className="text-red-300">red</span> — missed · grey — still
            ahead of you.
          </LI>
          <LI>The outlined square is today.</LI>
        </UL>
        <H>Weekly review</H>
        <P>
          Every Monday a short review card appears at the top: the last seven
          days, how many you finished, goals completed and your streak. Dismiss
          it and it stays gone for that week.
        </P>
      </>
    ),
  },
  {
    id: "goals",
    label: "Goals & sub-goals",
    icon: Target,
    body: (
      <>
        <P>
          Goals are grouped into <strong>tabs</strong> — think of them as
          projects or areas of life. The "To Do" tab is always there; add your
          own with the + next to the tab strip.
        </P>
        <H>Adding goals</H>
        <UL>
          <LI>Type one goal and press Enter.</LI>
          <LI>
            Paste several lines to create several goals at once. Lines that are
            indented (two spaces or a tab) become <strong>sub-goals</strong> of
            the line above, and everything you paste can share one deadline.
          </LI>
        </UL>
        <H>Deadlines and importance</H>
        <P>
          Give a goal a deadline and it shows up in <strong>Reminders</strong>{" "}
          automatically. Mark it important (star) to keep it at the top. A goal
          with sub-goals shows its progress, and completing the last sub-goal
          offers to complete the parent.
        </P>
        <P>
          Sub-goals sort themselves: open ones sit on top (earliest deadline
          first, then undated ones), and ticked ones drop to the bottom, most
          recently ticked first. Once a goal has more than three ticked
          sub-goals they fold into a <strong>Completed (N)</strong> row you can
          click open.
        </P>
        <Tip>
          Links you type inside a goal become clickable, and a date written as{" "}
          <strong>dd-mm-yyyy</strong> is highlighted in red so it stands out.
        </Tip>
      </>
    ),
  },
  {
    id: "reminders",
    label: "Reminders",
    icon: Bell,
    body: (
      <>
        <P>
          Reminders is a read-only overview: it gathers everything that has a
          date, so you never have to hunt through panels. It pulls in:
        </P>
        <UL>
          <LI>goal and sub-goal deadlines,</LI>
          <LI>
            Remember items with a due date (including the next occurrence of a
            repeating one),
          </LI>
          <LI>Quick Links cards you flagged to remember,</LI>
          <LI>conference submission deadlines,</LI>
          <LI>publication decision and camera-ready dates,</LI>
          <LI>sticky notes with a date.</LI>
        </UL>
        <P>
          Items are grouped as overdue, today, tomorrow and the next days. Click
          one to jump to where it lives. Use the toggle in the Remember panel if
          you would rather keep dated Remember items out of this list.
        </P>
      </>
    ),
  },
  {
    id: "remember",
    label: "Remember",
    icon: ListChecks,
    body: (
      <>
        <P>
          A simple checklist for things that are not goals: "water the plants",
          "renew the pass". Add a due date if it matters.
        </P>
        <H>Repeating items</H>
        <P>
          Switch an item to <strong>repeats weekly</strong>, pick the weekdays
          and an end date. Xunzhine then tracks each occurrence separately:
          ticking it marks only that day done and the next occurrence appears.
          Ending the series stops it for good.
        </P>
      </>
    ),
  },
  {
    id: "clipboard",
    label: "Clipboard",
    icon: Clipboard,
    body: (
      <>
        <P>
          Cards for text you want at hand — snippets, addresses, a packing list.
          Each line in a card can be plain text, a bullet, a numbered item or a
          checkbox, and you can mix them freely.
        </P>
        <UL>
          <LI>Click a card to edit it; changes save as you type.</LI>
          <LI>The copy button puts the whole card on your system clipboard.</LI>
          <LI>Drag cards by their handle to reorder them.</LI>
        </UL>
      </>
    ),
  },
  {
    id: "quicklinks",
    label: "Quick Links",
    icon: Link2,
    body: (
      <>
        <P>
          Groups of links with a description each — reading lists, applications,
          references. Tick a link when you have dealt with it; when every link
          in a card is ticked the card marks itself complete and moves down.
        </P>
        <P>
          Give a card a due date and switch on{" "}
          <strong>show in Reminders</strong> to have it appear alongside your
          deadlines.
        </P>
      </>
    ),
  },
  {
    id: "conferences",
    label: "Conferences",
    icon: GraduationCap,
    body: (
      <>
        <P>
          An optional panel (switch it on in Settings) for tracking academic
          conferences. One card per conference, with one{" "}
          <strong>edition</strong> per year: venue, dates, website, paper,
          authors, fields and the submission deadline.
        </P>
        <H>Status</H>
        <P>
          The status is worked out for you — Start, In Progress, Submitted,
          Awaiting Decision, Accepted, Rejected, Missed Deadline or Dead — based
          on the deadline, whether you submitted and the outcome. You can
          override it by hand when reality disagrees.
        </P>
        <P>
          Upcoming deadlines of editions you have not submitted to appear in
          Reminders. Sort the panel by deadline, field, live-first or by when
          you added or edited a card.
        </P>
      </>
    ),
  },
  {
    id: "publications",
    label: "Publications",
    icon: BookOpen,
    body: (
      <>
        <P>
          Also optional. One card per paper, holding the{" "}
          <strong>current submission attempt</strong> (conference or journal,
          dates, quartile, impact factor, notes) plus a full history of earlier
          attempts.
        </P>
        <H>Resubmitting</H>
        <P>
          "Resubmit to new venue" files the current attempt into the history and
          starts a fresh one. Nothing is deleted — rejected attempts stay on
          record. Which attempt shows as current is derived from the statuses
          and dates, so an accepted attempt always outranks a new draft.
        </P>
        <P>Decision dates and camera-ready dates feed into Reminders.</P>
      </>
    ),
  },
  {
    id: "notes",
    label: "Sticky notes & the board",
    icon: StickyNote,
    body: (
      <>
        <P>
          Sticky notes float over the page like paper on a desk. Add one with
          the + at the bottom-left; drag it by its top bar, resize from the
          bottom-right corner, and use the coloured dots to change its colour.
        </P>
        <H>Pinned or floating</H>
        <UL>
          <LI>
            <strong>Pinned</strong> notes belong to a panel and scroll with it —
            handy for a note about your goals.
          </LI>
          <LI>
            <strong>Floating</strong> notes stay put on screen while you scroll.
            The pin button switches between the two.
          </LI>
          <LI>
            The small <strong>▲ / ▼</strong> arrows in a note's header move it
            to the top of the panel above or below and pin it there (collapsed
            panels and the board are skipped).
          </LI>
        </UL>
        <H>Writing in a note</H>
        <P>
          Notes understand light Markdown: <Key>#</Key> headings, <Key>-</Key>{" "}
          bullets, <Key>1.</Key> numbered lists, <Key>[ ]</Key> checkboxes you
          can tick straight from the note, plus **bold**, *italic*,
          __underline__, ~~strike~~ and `code`. The toolbar in edit mode writes
          all of that for you, and <Key>Ctrl</Key>+<Key>B</Key> / <Key>I</Key> /{" "}
          <Key>U</Key> work as usual.
        </P>
        <H>The Sticky Notes board</H>
        <P>
          The board is a parking area for notes you want in one place. Press{" "}
          <strong>Send to board</strong> on a note (or drag it onto the board)
          and it takes the next free slot inside the dashed area. On the board:
        </P>
        <UL>
          <LI>notes always stay inside the dashed boundary,</LI>
          <LI>
            they never cover each other — drop one on an occupied spot and it
            settles into the nearest free slot,
          </LI>
          <LI>the board grows taller as more notes arrive,</LI>
          <LI>
            <strong>Go back</strong> on a note returns it to exactly where it
            was before, and <strong>Restore all</strong> does that for every
            note on the board.
          </LI>
        </UL>
        <P>
          Dragging a note off the board and onto another panel takes it off the
          board. Give a note a date and it joins your Reminders.
        </P>
      </>
    ),
  },
  {
    id: "focus",
    label: "Focus & Pomodoro",
    icon: Timer,
    body: (
      <>
        <P>
          The floating timer runs 25 minutes of focus and a 5 minute break by
          default. It keeps running while you use the rest of the page and
          survives a reload.
        </P>
        <P>
          <strong>Focus mode</strong> (<Key>F</Key>) strips the page down to
          your goals, the Mark today button and the timer. Press <Key>Esc</Key>{" "}
          to come back.
        </P>
      </>
    ),
  },
  {
    id: "sync",
    label: "Sync, backups & privacy",
    icon: Cloud,
    body: (
      <>
        <P>
          The cloud icon in the header shows how your data is stored: a coloured
          cloud means you are signed in and syncing, the disk icon means this
          browser only.
        </P>
        <H>How syncing works</H>
        <P>
          Changes are saved locally first, so the app keeps working offline, and
          are sent to the cloud as soon as it is reachable. If you edit the same
          thing on two devices, the most recent edit wins — per item, not per
          page, so you never lose a whole panel to a conflict.
        </P>
        <H>Version history</H>
        <P>
          Settings → Version History saves a full copy of your data on a
          schedule you choose (every N days at a local hour, keeping up to 100
          versions), plus “Save a version now” and “Clear all versions”. A
          scheduled save is skipped when nothing changed. You can look at a
          version before restoring it, and restoring itself saves a version
          first, so it is always undoable.
        </P>
        <H>Cloud usage</H>
        <P>
          Only real edits are sent to the cloud (a short moment after you stop
          typing). Opening or closing panels, switching themes or clicking a
          note to bring it forward are batched and sent at most once a minute.
          The app checks for changes from other devices when you come back to
          the tab and, while it stays visible, every ten minutes.
        </P>
        <H>Export</H>
        <P>
          Export everything as Markdown, CSV or a JSON backup, or share a
          progress card image, from Settings.
        </P>
        <H>Privacy</H>
        <P>
          Analytics only run if you accept cookies, and you can change that any
          time with "Cookie settings" in the footer. The Privacy Policy and
          Terms are linked there too.
        </P>
      </>
    ),
  },
  {
    id: "ai",
    label: "AI companion",
    icon: Sparkles,
    body: (
      <>
        <P>
          Pro (cloud) accounts get an optional AI companion — called{" "}
          <strong>xyz Ai</strong> until you rename it — that can answer
          questions about your own data, write briefings, summarise or rewrite
          notes, translate, and propose changes. It only ever acts when you ask,
          and every proposed change waits for your Confirm.
        </P>
        <H>Setting it up</H>
        <UL>
          <LI>
            Open Settings → AI companion and paste an API key for one or more
            providers: OpenRouter, Anthropic (Claude), Google (Gemini), OpenAI
            (GPT) or Z.ai (GLM). Keys are encrypted on the server and never
            sent back to the browser.
          </LI>
          <LI>
            Pick the preferred provider and a model from the live list, then
            press <strong>Test connection</strong>: it reports latency, tokens,
            estimated cost and whether the model accepts temperature /
            max-tokens (unsupported parameters are dropped automatically).
          </LI>
          <LI>
            Choose which panels it may read, a monthly budget (it warns at 80 %
            and stops at 100 %) and, if you like, voice: read replies aloud or
            dictate with the microphone (both use your browser, no extra keys).
          </LI>
        </UL>
        <H>Waking it up</H>
        <P>
          The ball next to the cloud icon in the header wakes or sleeps the
          companion. Awake, it hatches as a little egg that wanders the page —
          drag it anywhere, click it to chat. Each panel also has an{" "}
          <strong>Ask AI</strong> button that opens the chat focused on that
          panel. On phones the chat opens as a sheet from the bottom.
        </P>
        <H>What it can do</H>
        <UL>
          <LI>
            Answer questions (“what is due this week?”), give a <strong>Briefing</strong>{" "}
            (the sunrise button), summarise, rewrite, translate, or just chat.
          </LI>
          <LI>
            Propose actions: add / complete / reschedule / delete goals and
            sub-goals, sticky notes, Remember items, clipboard and quick-link
            cards, or mark a day. Each appears as a card with Confirm — nothing
            changes until you press it, and deletes keep the usual Undo.
          </LI>
          <LI>
            Long replies are continued automatically so they are never cut off.
            Every reply shows its tokens and approximate cost; the month’s totals
            are in Settings. Chat history stays in this browser only (Clear with
            the bin icon).
          </LI>
        </UL>
      </>
    ),
  },
  {
    id: "passkey",
    label: "Passkey & recovery",
    icon: KeyRound,
    body: (
      <>
        <P>
          Pro accounts sign in with a username and passkey. In Settings →
          Passkey you can <strong>change the passkey</strong> (current + new
          twice). By default this also signs out your other devices; the new
          passkey then works everywhere, including the Android app.
        </P>
        <H>Recovery codes</H>
        <P>
          Changing the passkey issues ten one-time <strong>recovery codes</strong>,
          shown only once — copy or download them. If you ever forget your
          passkey, use <strong>Forgot passkey?</strong> on the sign-in screen
          with one code to set a new passkey; the code is used up and every
          other device is signed out. You can issue a fresh set from Settings at
          any time (older codes stop working).
        </P>
        <Tip>
          Until you change it, the account uses the passkey configured on the
          server; after the first change only the new passkey works.
        </Tip>
      </>
    ),
  },
  {
    id: "shortcuts",
    label: "Keyboard & layout",
    icon: Keyboard,
    body: (
      <>
        <H>Shortcuts</H>
        <UL>
          <LI>
            <Key>Ctrl</Key>+<Key>K</Key> — command palette: jump anywhere, add
            anything, run any action by typing its name.
          </LI>
          <LI>
            <Key>T</Key> — mark today · <Key>N</Key> — jump to the new-goal box
            · <Key>/</Key> — search goals · <Key>F</Key> — focus mode ·{" "}
            <Key>W</Key> — weekly review · <Key>?</Key> — command palette.
          </LI>
          <LI>
            <Key>Esc</Key> closes whatever is open and leaves focus mode.
          </LI>
          <LI>
            Shortcuts are ignored while you are typing in a field. To add a
            sticky note, use the + at the bottom-left or "New sticky note" in
            the command palette.
          </LI>
        </UL>
        <H>Arranging the page</H>
        <UL>
          <LI>
            Every panel has a slim bar with <strong>Minimize</strong>; minimized
            panels stay out of the way until you expand them again.
          </LI>
          <LI>
            The up/down arrows on a panel move it in the page order. Your order
            is remembered and synced.
          </LI>
          <LI>
            The rail of dots on the left jumps to a panel; you can move it to
            the other side in Settings.
          </LI>
          <LI>
            An undo toast appears after deletions — one click brings the item
            back.
          </LI>
        </UL>
      </>
    ),
  },
  {
    id: "mobile",
    label: "Phone & Android app",
    icon: Smartphone,
    body: (
      <>
        <P>
          The website works on a phone: panels stack, and sticky notes appear as
          a simple list instead of floating cards.
        </P>
        <P>
          There is also an Android app that signs in with the same username and
          passkey and shows the same data. On top of the website it adds a
          home-screen widget with your streak and a Mark today button, local
          reminders, and <strong>share to Xunzhine</strong> — share a link from
          any app and pick where it should land (Inbox, a goal, Remember,
          Clipboard, Quick Links or a sticky note).
        </P>
      </>
    ),
  },
  {
    id: "settings",
    label: "Settings",
    icon: SettingsIcon,
    body: (
      <>
        <UL>
          <LI>
            <strong>Challenge</strong> — name, start date, number of days.
          </LI>
          <LI>
            <strong>Panels</strong> — switch Conferences and Publications on or
            off.
          </LI>
          <LI>
            <strong>Appearance</strong> — theme (follows your system by
            default), skin, background effects, calendar density.
          </LI>
          <LI>
            <strong>Reminder hour</strong> — when the streak reminder should
            nudge you (also used by the Android app).
          </LI>
          <LI>
            <strong>Data</strong> — export, version history, and signing out.
          </LI>
        </UL>
        <Tip>
          Settings are stored with your account, so the same preferences follow
          you to another browser or to the phone.
        </Tip>
      </>
    ),
  },
];

const textOf = (node) => {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(" ");
  if (node.props && node.props.children) return textOf(node.props.children);
  return "";
};

/** Full user guide, opened from the footer. */
const HelpModal = ({ open, onClose }) => {
  const [active, setActive] = useState(SECTIONS[0].id);
  const [query, setQuery] = useState("");

  const haystacks = useMemo(
    () =>
      SECTIONS.map((s) => ({
        id: s.id,
        text: `${s.label} ${textOf(s.body)}`.toLowerCase(),
      })),
    [],
  );
  const q = query.trim().toLowerCase();
  const matches = q
    ? new Set(haystacks.filter((h) => h.text.includes(q)).map((h) => h.id))
    : null;
  const shown = matches
    ? SECTIONS.filter((s) => matches.has(s.id))
    : SECTIONS.filter((s) => s.id === active);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 bg-black/80">
      <div className="bg-gray-800 w-full max-w-5xl max-h-[90vh] rounded-3xl border-2 border-cyan-500/30 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <HelpCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-cyan-400 truncate">
              How to use Xunzhine
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-700 text-gray-400 hover:text-cyan-300 transition-colors"
            aria-label="Close help"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide — try “streak”, “board”, “export”…"
              className="w-full bg-gray-700/60 text-cyan-100 pl-9 pr-3 py-2.5 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm placeholder-gray-500"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col sm:flex-row gap-4 p-5">
          {/* Contents */}
          {!q && (
            <nav className="sm:w-56 flex-shrink-0 sm:overflow-y-auto">
              <ul className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible pb-1">
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  const on = s.id === active;
                  return (
                    <li key={s.id} className="flex-shrink-0">
                      <button
                        onClick={() => setActive(s.id)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors ${
                          on
                            ? "bg-cyan-500/15 text-cyan-200 border border-cyan-500/40"
                            : "text-gray-300 hover:bg-gray-700/60 border border-transparent"
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{s.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}

          {/* Body */}
          <div className="flex-1 min-w-0 overflow-y-auto pr-1">
            {shown.length === 0 && (
              <p className="text-gray-400 text-sm">
                Nothing in the guide matches “{query}”. Try a different word —
                or ask for help at contact@xunzhine.in.
              </p>
            )}
            {shown.map((s) => {
              const Icon = s.icon;
              return (
                <section key={s.id} className="mb-6 last:mb-0">
                  <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5" />
                    {s.label}
                  </h3>
                  {s.body}
                </section>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-700 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-2">
          <span>
            Still stuck? Write to{" "}
            <span className="text-cyan-300">contact@xunzhine.in</span>
          </span>
          <span>
            Press <Key>Ctrl</Key>+<Key>K</Key> in the app to find any action by
            name.
          </span>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
